import React, { useState } from 'react';
import { ANDAData } from '../../MoleculeSearch.type';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { FilterMatchMode, FilterService } from 'primereact/api';
import { Calendar } from 'primereact/calendar';
import { Skeleton } from 'primereact/skeleton';
import { ANDADetailModal } from 'components/shared/ANDADetailModal';
import { ANDAInfoCardData } from 'Interfaces/ProductWikiInterfaces';
import './ANDAInformationSection.styles.scss';
import pillsIcon from 'assets/icons/pillsIcon.svg';

// Custom date range filter function
const dateRangeFilterFunction = (
  value: string,
  filter: [Date | null, Date | null] | null
): boolean => {
  // No filter applied - show all rows
  if (!filter || !Array.isArray(filter)) {
    return true;
  }

  const [fromDate, toDate] = filter;

  // No dates selected - show all rows
  if (!fromDate && !toDate) {
    return true;
  }

  // No value to compare - hide row if filter is active
  if (!value) {
    return false;
  }

  // Parse the date string (assuming format like "MM/DD/YYYY" or "YYYY-MM-DD")
  const dateValue = new Date(value);
  if (isNaN(dateValue.getTime())) {
    return false; // Invalid date
  }

  // Normalize dates to start of day for comparison
  const normalizeDate = (date: Date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  };

  const normalizedValue = normalizeDate(dateValue);

  if (fromDate && toDate) {
    return normalizedValue >= normalizeDate(fromDate) && normalizedValue <= normalizeDate(toDate);
  } else if (fromDate) {
    return normalizedValue >= normalizeDate(fromDate);
  } else if (toDate) {
    return normalizedValue <= normalizeDate(toDate);
  }

  return true;
};

// Register custom date range filter with FilterService
FilterService.register('dateRange', dateRangeFilterFunction);

export interface ANDAInformationSectionProps {
  andaData: ANDAData[];
  loading?: boolean;
  error?: string | null;
}

/**
 * Skeleton loader for ANDA table using PrimeReact Skeleton component
 */
const ANDATableSkeleton: React.FC = () => (
  <Skeleton width="100%" height="600px" borderRadius="var(--border-radius)" />
);

// Initial filter configuration
const getInitialFilters = (): DataTableFilterMeta => ({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  marketStatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
  activeIngredient: { value: null, matchMode: FilterMatchMode.CONTAINS },
  proprietaryName: { value: null, matchMode: FilterMatchMode.CONTAINS },
  applicationNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
  dosageForm: { value: null, matchMode: FilterMatchMode.CONTAINS },
  route: { value: null, matchMode: FilterMatchMode.CONTAINS },
  strength: { value: null, matchMode: FilterMatchMode.CONTAINS },
  rld: { value: null, matchMode: FilterMatchMode.CONTAINS },
  rs: { value: null, matchMode: FilterMatchMode.CONTAINS },
  applicantHolder: { value: null, matchMode: FilterMatchMode.CONTAINS },
  marketingStatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
  lastSubmissionType: { value: null, matchMode: FilterMatchMode.CONTAINS },
  // Use the registered 'dateRange' filter name as matchMode
  lastSubmissionDate: { value: null, matchMode: 'dateRange' as FilterMatchMode },
});

const ANDAInformationSection: React.FC<ANDAInformationSectionProps> = ({ andaData, loading = false, error = null }) => {
  const [filters, setFilters] = useState<DataTableFilterMeta>(getInitialFilters());
  const [_globalFilterValue, _setGlobalFilterValue] = useState<string>('');
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [selectedAnda, setSelectedAnda] = useState<ANDAInfoCardData | null>(null);

  // Date range filter template for date columns
  const dateRangeFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    // Handle both array format and null
    const filterValue = options.value;
    const from = Array.isArray(filterValue) ? filterValue[0] : null;
    const to = Array.isArray(filterValue) ? filterValue[1] : null;

    return (
      <div
        className="date-range-filter"
        style={{ display: 'flex', gap: '8px', flexDirection: 'row', width: '100%' }}
      >
        <Calendar
          value={from}
          onChange={e => options.filterApplyCallback([e.value, to])}
          placeholder="From"
          dateFormat="mm/dd/yy"
          showIcon
          className="p-column-filter"
          style={{ width: '9rem' }}
        />
        <Calendar
          value={to}
          onChange={e => options.filterApplyCallback([from, e.value])}
          placeholder="To"
          dateFormat="mm/dd/yy"
          showIcon
          className="p-column-filter"
          style={{ width: '9rem' }}
        />
      </div>
    );
  };

  // const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const value = e.target.value;
  //   const _filters = { ...filters };
  //   (_filters['global'] as { value: string | null; matchMode: FilterMatchMode }).value = value;
  //   setFilters(_filters);
  //   setGlobalFilterValue(value);
  // };

  /**
   * Transform ANDAData to ANDAInfoCardData format for the modal
   */
  const transformToModalData = (anda: ANDAData): ANDAInfoCardData => {
    const isDiscontinued = anda.marketingStatus?.toLowerCase() === 'discontinued';
    const color = isDiscontinued ? '#4b5563' : '#10b981';

    return {
      title: `${anda.applicationNumber} - ${anda.activeIngredient} - ${anda.strength} - ${anda.route} - ${anda.dosageForm}`,
      date: anda.effectiveDate || 'N/A',
      description: `${anda.proprietaryName} - ${anda.strength}`,
      card_description: anda.proprietaryName
        ? `${anda.proprietaryName} - ${anda.strength}`
        : anda.applicationNumber ?? '',
      tags: [
        { status: anda.marketStatus, type: 'normal' },
        { applicant: anda.applicantHolder, type: 'normal' },
      ],
      priority: 'normal',
      application_number: anda.applicationNumber,
      approval_date: anda.effectiveDate || null,
      applicant: anda.applicantHolder,
      concept_id: null,
      color: color,
      link: anda.andaLink || null,
      effective_date: anda.effectiveDate || null,
      marketing_status: anda.marketingStatus || null,
      last_submission_date: anda.lastSubmissionDate || null,
      last_submission_type: anda.lastSubmissionType || null,
      history: anda.history || [],
    };
  };

  /**
   * Handle row click to open modal
   */
  const handleRowClick = (rowData: ANDAData) => {
    const modalData = transformToModalData(rowData);
    setSelectedAnda(modalData);
    setModalVisible(true);
  };

  /**
   * Handle modal close
   */
  const handleModalHide = () => {
    setModalVisible(false);
    setSelectedAnda(null);
  };

  /**
   * Format date string to "MMM DD, YY" format (e.g., "Dec 10, 20")
   */
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid

    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const month = months[date.getMonth()];
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2); // Get last 2 digits of year

    return `${month} ${day}, ${year}`;
  };

  const lastSubmissionDateBodyTemplate = (rowData: ANDAData) => {
    return <span>{formatDate(rowData.lastSubmissionDate)}</span>;
  };

  const applicationNumberBodyTemplate = (rowData: ANDAData) => {
    const isDiscontinued = rowData.marketingStatus?.toLowerCase() === 'discontinued';
    const colorClass = isDiscontinued ? 'anda-link-discontinued' : 'anda-link-active';

    // If there's a link, render as clickable anchor; otherwise render as plain text
    if (rowData.andaLink) {
      return (
        <a
          href={rowData.andaLink}
          target="_blank"
          rel="noopener noreferrer"
          className={`anda-link ${colorClass}`}
        >
          {rowData.applicationNumber}
        </a>
      );
    }

    return <span className={`anda-text ${colorClass}`}>{rowData.applicationNumber}</span>;
  };

  return (
    <section id="anda-information" className="content-section">
      <div className="section-header">
        <div className="section-title">
          <img src={pillsIcon} alt="Pills" />
          ANDA Information
        </div>
        <div className="anda-controls">
          <span className="page-info">
            {andaData.length} application{andaData.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
      <div className="section-content">
        {loading ? (
          <div className="anda-table-container">
            <ANDATableSkeleton />
          </div>
        ) : error ? (
          <div className="anda-error" style={{ padding: '2rem', textAlign: 'center' }}>
            <i className="fas fa-exclamation-triangle" style={{ color: 'var(--error-color)', marginRight: '0.5rem' }} />
            <span>{error}</span>
          </div>
        ) : (
        <div className="anda-table-container">
          <DataTable
            value={andaData}
            filters={filters}
            onFilter={e => setFilters(e.filters)}
            filterDisplay="row"
            emptyMessage="No ANDA data available"
            scrollable
            scrollHeight="900px"
            onRowClick={e => handleRowClick(e.data as ANDAData)}
            rowClassName={() => 'clickable-row'}
          >
            <Column
              field="applicationNumber"
              header="Appl. No."
              body={applicationNumberBodyTemplate}
              filter
              filterPlaceholder="Search Appl. No."
              showFilterMenu={false}
              showClearButton={true}
              style={{ minWidth: '150px' }}
            />
            <Column
              field="marketStatus"
              header="Market Status"
              filter
              filterPlaceholder="Search Market Status"
              showFilterMenu={false}
              showClearButton={true}
              style={{ minWidth: '150px' }}
            />
            <Column
              field="activeIngredient"
              header="Active Ingredient"
              filter
              filterPlaceholder="Search Active Ingredient"
              showFilterMenu={false}
              showClearButton={true}
              style={{ minWidth: '150px' }}
            />
            {/* <Column
              field="proprietaryName"
              header="Proprietary Name"
              filter
              filterPlaceholder="Search Proprietary Name"
              showFilterMenu={false}
              showClearButton={true}
              style={{ minWidth: '150px' }}
            /> */}

            <Column
              field="dosageForm"
              header="Dosage Form"
              filter
              filterPlaceholder="Search Dosage Form"
              showFilterMenu={false}
              showClearButton={true}
              style={{ minWidth: '150px' }}
            />
            <Column
              field="route"
              header="Route"
              filter
              filterPlaceholder="Search Route"
              showFilterMenu={false}
              showClearButton={true}
              style={{ minWidth: '150px' }}
            />
            <Column
              field="strength"
              header="Strength"
              filter
              filterPlaceholder="Search Strength"
              showFilterMenu={false}
              showClearButton={true}
              style={{ minWidth: '150px' }}
            />
            <Column
              field="rld"
              header="RLD"
              filter
              filterPlaceholder="Search RLD"
              showFilterMenu={false}
              showClearButton={true}
              style={{ minWidth: '150px' }}
            />
            <Column
              field="rs"
              header="RS"
              filter
              filterPlaceholder="Search RS"
              showFilterMenu={false}
              showClearButton={true}
              style={{ minWidth: '150px' }}
            />
            <Column
              field="applicantHolder"
              header="Applicant Holder"
              filter
              filterPlaceholder="Search Applicant Holder"
              showFilterMenu={false}
              showClearButton={true}
              style={{ minWidth: '150px' }}
            />
            <Column
              field="lastSubmissionType"
              header="Last Submission Type"
              filter
              filterPlaceholder="Search Submission Type"
              showFilterMenu={false}
              showClearButton={true}
              style={{ minWidth: '160px' }}
            />
            <Column
              field="lastSubmissionDate"
              header="Last Submission Date"
              body={lastSubmissionDateBodyTemplate}
              filter
              filterPlaceholder="Date Range"
              showFilterMenu={false}
              showClearButton={true}
              filterElement={dateRangeFilterTemplate}
              filterFunction={(value, filter) => dateRangeFilterFunction(value, filter)}
              style={{ minWidth: '250px' }}
            />
          </DataTable>
        </div>
        )}
      </div>

      {/* ANDA Detail Modal */}
      <ANDADetailModal visible={modalVisible} onHide={handleModalHide} data={selectedAnda} />
    </section>
  );
};

export default ANDAInformationSection;
